'use client'
import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react'
import { ReplacerObject, DictionaryModel, Dictionary, messageKeyToFallback } from '../../MyStichtingOpenHelpers/src'

export type GetTextModel = <T = undefined>(
    key: string,
    replacer?: ReplacerObject,
    fallbackReturnValue?: T,
) => string | T

interface DictionaryProviderValue {
    getText: GetTextModel
    /** @deprecated Don't use anymore, stick to using "MESSAGE_KEYS" as on all other places */
    filter: (key: string) => DictionaryModel
    areKeysVisible: boolean
}

export const DictionaryContext = createContext<DictionaryProviderValue>({
    getText: (key: string, ..._args: any) => key,
    filter: (_key: string) => ({}),
    areKeysVisible: false,
})

interface DictionaryProviderProps {
    children: ReactNode
    dictionary: DictionaryModel
}

/**
 * @deprecated prefer `useContext(DictionaryContext)`
 * */
export const useDictionary = (): DictionaryProviderValue => {
    return useContext(DictionaryContext)
}

// Extend the Window interface
declare global {
    interface Window {
        showDictionaryKeys: () => void
    }
}

export const DictionaryProvider = ({ children, dictionary }: DictionaryProviderProps): JSX.Element => {
    const [areKeysVisible, setKeysVisibility] = useState<boolean>(false)

    const showDictionaryKeys = (): void => {
        setKeysVisibility(true)
    }

    if (typeof window !== 'undefined') window.showDictionaryKeys = showDictionaryKeys

    const providerValue: DictionaryProviderValue = useMemo(
        () => ({
            getText: (key: string, replacer?: ReplacerObject, fallbackReturnValue?) =>
                Dictionary.getText(dictionary, key, replacer, fallbackReturnValue ?? messageKeyToFallback(key)),
            filter: (key: string) => Dictionary.filterDictionary(dictionary, key),
            areKeysVisible,
        }),
        [dictionary, areKeysVisible],
    )

    return <DictionaryContext.Provider value={providerValue}>{children}</DictionaryContext.Provider>
}
